
import { useEffect, useCallback, useRef, useState } from 'react';
import { useNavigate, Link } from 'react-router';
import { PrayerCard, EmptyState, LoadingState, ErrorState } from '@/components/prayer';
import { usePrayerViewer } from '@/hooks/usePrayerViewer';
import { cn } from '@/lib/utils';

/**
 * PrayPage - Congregant Prayer Viewer
 * 
 * A card-based interface where users can scroll through
 * prayer requests one at a time, joining others in prayer.
 */
export default function PrayPage() {
  const navigate = useNavigate();
  const containerRef = useRef<HTMLDivElement>(null);
  const [isOffline, setIsOffline] = useState(typeof navigator !== 'undefined' ? !navigator.onLine : false);

  // Use the prayer viewer hook for state management
  const {
    currentPrayer,
    isLoading,
    error,
    hasNewPrayers,
    prayersViewed,
    fetchNextPrayer,
    clearNewPrayersNotification,
    isConnected,
    isViewLimitReached,
    cooldownSecondsRemaining,
    isPacingCooldownActive,
    shouldShowPacingReminder,
  } = usePrayerViewer({
    enableRealtime: true,
    onNewPrayerAvailable: () => {
      // Could show a toast notification here
    },
  });

  useEffect(() => {
    const handleOnline = () => setIsOffline(false);
    const handleOffline = () => setIsOffline(true);
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    if (typeof navigator !== 'undefined' && !navigator.onLine) setIsOffline(true);
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  const displayPrayer = currentPrayer ?? null;
  const canAdvanceOnline = !isViewLimitReached && !isPacingCooldownActive;
  const handleFetchNext = !isOffline && canAdvanceOnline ? fetchNextPrayer : () => {};

  // Handle keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Navigate with arrow right or space (only if not in cooldown)
      if ((e.key === 'ArrowRight' || e.key === ' ') && !isLoading && canAdvanceOnline) {
        e.preventDefault();
        handleFetchNext();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleFetchNext, isLoading, canAdvanceOnline]);

  // Handle touch swipe for mobile
  const touchStartX = useRef<number | null>(null);
  const touchEndX = useRef<number | null>(null);
  const minSwipeDistance = 50;

  const onTouchStart = useCallback((e: React.TouchEvent) => {
    touchEndX.current = null;
    const touch = e.targetTouches[0];
    if (touch) touchStartX.current = touch.clientX;
  }, []);

  const onTouchMove = useCallback((e: React.TouchEvent) => {
    const touch = e.targetTouches[0];
    if (touch) touchEndX.current = touch.clientX;
  }, []);

  const onTouchEnd = useCallback(() => {
    if (!touchStartX.current || !touchEndX.current) return;
    
    const distance = touchStartX.current - touchEndX.current;
    const isLeftSwipe = distance > minSwipeDistance;
    
    if (isLeftSwipe && !isLoading && canAdvanceOnline) {
      handleFetchNext();
    }
    
    touchStartX.current = null;
    touchEndX.current = null;
  }, [handleFetchNext, isLoading, canAdvanceOnline]);

  // Navigation handlers
  const handleGoHome = useCallback(() => {
    navigate('/');
  }, [navigate]);

  const handleSubmitPrayer = useCallback(() => {
    navigate('/');
  }, [navigate]);

  const handleRetry = useCallback(() => {
    if (!isOffline) fetchNextPrayer();
  }, [isOffline, fetchNextPrayer]);

  return (
    <>
      <main 
        ref={containerRef}
        className={cn(
          "min-h-screen",
          "bg-gradient-to-b from-slate-50 to-slate-100",
          "dark:from-slate-900 dark:to-slate-800",
          "flex flex-col"
        )}
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
      >
        {/* Page Header */}
        <header className="p-4 sm:p-6 text-center relative">
          <Link to="/" className="absolute top-4 left-4 inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-300">
            <span aria-hidden>←</span> Back
          </Link>
          {/* Connection status indicator */}
          <div 
            className={cn(
              "absolute top-4 right-4 flex items-center gap-2",
              "text-xs text-slate-500 dark:text-slate-400"
            )}
            title={isOffline ? 'Offline' : isConnected ? 'Connected - real-time updates enabled' : 'Connecting...'}
          >
            <span 
              className={cn(
                "w-2 h-2 rounded-full",
                isOffline ? "bg-amber-500" : isConnected ? "bg-green-500" : "bg-slate-400"
              )}
              aria-hidden="true"
            />
            {isOffline && (
              <span className="text-amber-600 dark:text-amber-400 font-medium">
                Offline
              </span>
            )}
            <span className="sr-only">
              {isOffline ? 'Offline' : isConnected ? 'Connected' : 'Disconnected'}
            </span>
          </div>

          <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 dark:text-white">
            Pray With Us
          </h1>
          <p className="text-sm sm:text-base text-slate-600 dark:text-slate-400 mt-1">
            Join others in lifting up these prayers
          </p>
        </header>

        {/* New prayers notification (aria-live for screen readers) */}
        {hasNewPrayers && currentPrayer && (
          <div aria-live="polite">
          <button
            onClick={() => {
              clearNewPrayersNotification();
              fetchNextPrayer();
            }}
            className={cn(
              "mt-3 inline-flex items-center gap-2 px-4 py-2",
              "bg-primary-100 dark:bg-primary-900/30",
              "text-primary-700 dark:text-primary-300",
              "rounded-full text-sm font-medium",
              "hover:bg-primary-200 dark:hover:bg-primary-900/50",
              "transition-colors animate-pulse"
            )}
          >
            <span className="w-2 h-2 bg-primary-500 rounded-full" aria-hidden="true" />
            New prayers available
          </button>
          </div>
        )}

        {/* Main content area */}
      <div 
        className={cn(
          "flex-1 flex items-center justify-center",
          "p-4 sm:p-6"
        )}
      >
        {isLoading && !displayPrayer ? (
          <LoadingState />
        ) : isOffline && !displayPrayer ? (
          <div className="text-center p-8">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center">
              <svg className="w-8 h-8 text-amber-600 dark:text-amber-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 5.636a9 9 0 010 12.728m0 0l-2.829-2.829m2.829 2.829L21 21M15.536 8.464a5 5 0 010 7.072m0 0l-2.829-2.829m-4.243 2.829a4.978 4.978 0 01-1.414-2.83m-1.414 5.658a9 9 0 01-2.167-9.238m7.824 2.167a1 1 0 111.414 1.414m-1.414-1.414L3 3m8.293 8.293l1.414 1.414" />
              </svg>
            </div>
            <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-2">
              You&apos;re Offline
            </h3>
            <p className="text-slate-600 dark:text-slate-400 mb-4">
              Connect to the internet to view prayers.
            </p>
            <button
              onClick={handleGoHome}
              className="text-primary-600 dark:text-primary-400 font-medium hover:underline"
            >
              Go to Home
            </button>
          </div>
        ) : error && !displayPrayer ? (
          <ErrorState 
            message={error}
            onRetry={handleRetry}
            onGoHome={handleGoHome}
          />
        ) : error && displayPrayer ? (
          <div className="w-full flex flex-col items-center gap-4">
            <PrayerCard
              prayer={displayPrayer}
              onNext={handleFetchNext}
              isLoading={isLoading}
              isNextDisabled={isViewLimitReached || isPacingCooldownActive}
              nextLabel={isViewLimitReached ? 'Come back tomorrow' : 'Next Prayer'}
              cooldownSecondsRemaining={cooldownSecondsRemaining}
              shouldShowPacingReminder={false}
              showCategory={false}
            />
            <div className="max-w-md w-full text-center text-sm text-amber-700 dark:text-amber-300 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-xl p-4">
              {error}
            </div>
          </div>
        ) : !displayPrayer ? (
          <EmptyState 
            onRefresh={handleRetry}
            onSubmitPrayer={handleSubmitPrayer}
          />
        ) : (
          <PrayerCard
            prayer={displayPrayer}
            onNext={handleFetchNext}
            isLoading={isLoading}
            isNextDisabled={isViewLimitReached || isPacingCooldownActive}
            nextLabel={isViewLimitReached ? 'Come back tomorrow' : 'Next Prayer'}
            cooldownSecondsRemaining={cooldownSecondsRemaining}
            shouldShowPacingReminder={false}
            showCategory={false}
          />
        )}
      </div>

      {/* Footer with navigation hints and stats */}
      <footer 
        className={cn(
          "p-4 text-center",
          "text-sm text-slate-500 dark:text-slate-400"
        )}
      >
        {displayPrayer && (
          <>
            <p className="mb-2">
              <span className="hidden sm:inline">Tap Next to continue • </span>
              <span className="sm:hidden">Swipe left or tap Next to continue</span>
              <span className="hidden sm:inline">
                Swipe left on mobile
              </span>
            </p>
            {/*prayersViewed > 0 && (
              <p className="text-xs text-slate-400 dark:text-slate-500">
                You&apos;ve prayed over {prayersViewed} {prayersViewed === 1 ? 'request' : 'requests'} today
              </p>
            )*/}
          </>
        )}
      </footer>
      </main>
    </>
  );
}
